// src/services/api.js - Baroom API Service
// Follows exact same pattern as Nidoux ApiService
import { getApiBaseUrl, getAppIdentifier } from '../config';
import i18n from 'i18next';

const API_BASE = getApiBaseUrl();

class ApiService {
  constructor() {
    // 429 backoff state: when we get rate-limited, pause ALL requests briefly
    this._backoffUntil = 0;
  }

  // Centralized request method with credentials, app identifier, and 429 backoff
  async request(endpoint, options = {}) {
    // If we're in a backoff period, reject immediately to avoid flooding
    const now = Date.now();
    if (now < this._backoffUntil) {
      const error = new Error('Rate limited – backing off');
      error.status = 429;
      error.isBackoff = true;
      throw error;
    }

    try {
      const appId = getAppIdentifier();

      const response = await fetch(`${API_BASE}${endpoint}`, {
        ...options,
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-App-Identifier': appId || '',
          'Accept-Language': i18n.language || 'en',
          ...options.headers,
        },
      });

      // Handle 429 Too Many Requests – enable backoff for ALL subsequent requests
      if (response.status === 429) {
        const retryAfter = response.headers.get('Retry-After');
        const backoffMs = retryAfter ? parseInt(retryAfter, 10) * 1000 : 60000;
        this._backoffUntil = Date.now() + backoffMs;
        console.warn(`429 Rate Limited on ${endpoint} – backing off for ${backoffMs / 1000}s`);
        const error = new Error('Too many requests, please try again later.');
        error.status = 429;
        throw error;
      }

      if (!response.ok) {
        try {
          const errorData = await response.json();
          const error = new Error(errorData.message || `Request failed with status ${response.status}`);
          error.status = response.status;
          // Machine-readable error code (e.g. 'conflict', 'payment_required') so
          // callers can branch reliably without parsing the human message.
          if (errorData.error) error.code = errorData.error;
          if (errorData.validationErrors) error.validationErrors = errorData.validationErrors;
          if (errorData.errors) error.validationErrors = errorData.errors;
          if (errorData.validationWarnings) error.validationWarnings = errorData.validationWarnings;
          if (errorData.warnings) error.validationWarnings = errorData.warnings;
          if (errorData.details) error.details = errorData.details;
          if (errorData.conflicts) error.conflicts = errorData.conflicts;
          throw error;
        } catch (jsonError) {
          if (jsonError instanceof Error && (jsonError.status || jsonError.validationErrors || jsonError.details || jsonError.code)) {
            throw jsonError;
          }
          const error = new Error(`Request failed with status ${response.status}: ${response.statusText}`);
          error.status = response.status;
          throw error;
        }
      }

      return await response.json();
    } catch (error) {
      if (error.status && error.status >= 500) {
        console.error('API Server Error:', error);
      }
      throw error;
    }
  }

  // ─── Authentication ───────────────────────────────────────────────

  async login(accessCode) {
    return this.request('/login', {
      method: 'POST',
      body: JSON.stringify({ accessCode }),
    });
  }

  async verifySmsCode(userId, code) {
    return this.request('/login/verify-sms-code', {
      method: 'POST',
      body: JSON.stringify({ userId, code }),
    });
  }

  async tokenAuth(rememberToken) {
    return this.request('/login/token-auth', {
      method: 'POST',
      body: JSON.stringify({ rememberToken }),
    });
  }

  async logout() {
    // Use raw fetch since this returns a redirect, not JSON
    const appId = getAppIdentifier();
    await fetch(`${API_BASE}/logout`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'X-App-Identifier': appId || ''
      }
    });
  }

  // ─── Users ────────────────────────────────────────────────────────

  async getUsers() {
    return this.request('/users');
  }

  async getUser(userId) {
    return this.request(`/users/${userId}`);
  }

  async createUser(data) {
    return this.request('/users/create', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateUser(userId, data) {
    return this.request(`/users/update/${userId}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteUser(userId) {
    return this.request(`/users/delete/${userId}`, {
      method: 'DELETE',
    });
  }

  async checkAccessCode(accessCode) {
    return this.request(`/users/check-access-code/${accessCode}`);
  }

  // Session-scoped email notification preferences for the logged-in user.
  // Returns { emailNotifications, notifyBookingConfirmation,
  // notifyBookingReminders, notifyBookingCancellation } (booleans; a missing
  // flag is treated as true server-side).
  async getNotificationPrefs() {
    return this.request('/users/notification-prefs');
  }

  // Updates any subset of the four email notification flags for the logged-in
  // user and returns the updated four. Partial bodies are allowed.
  async updateNotificationPrefs(data) {
    return this.request('/users/notification-prefs', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // ─── Rooms ────────────────────────────────────────────────────────

  async getRooms() {
    return this.request('/rooms');
  }

  async getRoom(roomId) {
    return this.request(`/rooms/${roomId}`);
  }

  // Cross-room availability search. Returns rooms annotated with `free` and
  // `conflictUntil`, sorted free-first then by name (server-side).
  // amenities is an array; sent as a comma-separated CSV query param.
  async searchAvailability({ start, end, capacity, amenities } = {}) {
    const params = new URLSearchParams();
    if (start) params.set('start', start);
    if (end) params.set('end', end);
    if (capacity !== undefined && capacity !== null && capacity !== '') {
      params.set('capacity', String(capacity));
    }
    if (Array.isArray(amenities) && amenities.length > 0) {
      params.set('amenities', amenities.join(','));
    }
    return this.request(`/rooms/availability?${params}`);
  }

  // Server-side booking analytics over the BookingOccurrence collection,
  // scoped to the session admin and an optional [from, to] window (ISO dates;
  // defaults server-side to the last 90 days). Returns a compact JSON with
  // totalBookings, bookedHours, cancellationRate, noShowRate, byHourOfDay[24],
  // byDayOfWeek[7] (idx 0 = Monday), byRoom[], and distinctUsers. Replaces the
  // prior client-side aggregation of ALL occurrences (no date bound).
  async getRoomAnalytics(from, to) {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    const qs = params.toString();
    return this.request(`/rooms/analytics${qs ? `?${qs}` : ''}`);
  }

  async createRoom(data) {
    return this.request('/rooms/create', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateRoom(roomId, data) {
    // PATCH = full update; backend persists all provided fields (name,
    // capacity, amenities, description, …) and is adminId-scoped server-side.
    return this.request(`/rooms/update/${roomId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async updateRoomPayable(roomId, isPayable) {
    return this.request(`/rooms/update-payable/${roomId}`, {
      method: 'POST',
      body: JSON.stringify({ isPayable }),
    });
  }

  // Enable/disable a room's public (Calendly-style) booking link (admin-authed,
  // tenant-scoped server-side). Mints the token on first enable. Resolves to
  // { enabled, token, url } where url is "<site>/?publicBook=<token>".
  async setRoomPublicLink(roomId, enabled) {
    return this.request(`/rooms/${roomId}/public-link`, {
      method: 'POST',
      body: JSON.stringify({ enabled }),
    });
  }

  // ─── Public booking (no account) ──────────────────────────────────
  // These hit the unauthenticated /public-book routes (token in the path is the
  // capability). They still go through request() so the app-identifier header +
  // 429 backoff apply; the server ignores the session on these routes.

  // Resolve a public booking link: returns { room, occupied } (safe room info +
  // that room's upcoming occupied slots). Throws (status 404) for a bad/disabled
  // token.
  async publicGetRoom(token) {
    return this.request(`/public-book/${token}`);
  }

  // Create a no-account booking on a public link. data = { name, email,
  // startDate, endDate }. Resolves to { booking } on success; throws with
  // status 409 (conflict or payment_required) / 400 (validation) otherwise.
  async publicBook(token, data) {
    return this.request(`/public-book/${token}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteRoom(roomId) {
    return this.request(`/rooms/delete/${roomId}`, {
      method: 'DELETE',
    });
  }

  async generateRoomQR(roomId) {
    return this.request(`/rooms/generate-qr/${roomId}`, {
      method: 'POST',
    });
  }

  async uploadRoomPhoto(roomId, file) {
    const appId = getAppIdentifier();
    const formData = new FormData();
    formData.append('image', file);

    const response = await fetch(`${API_BASE}/rooms/upload-photo/${roomId}`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'X-App-Identifier': appId || '',
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Upload failed');
    }
    return response.json();
  }

  async assignRoomToBox(spaceId, roomId, boxId) {
    return this.request('/rooms/assign', {
      method: 'POST',
      body: JSON.stringify({ spaceId, roomId, boxId }),
    });
  }

  async removeBoxAssignment(roomId, boxId) {
    return this.request('/rooms/removeBoxAssignment', {
      method: 'POST',
      body: JSON.stringify({ roomId, boxId }),
    });
  }

  // ─── Bookings ─────────────────────────────────────────────────────

  async bookRoom(data) {
    return this.request('/rooms/book', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getBookingOccurrences(roomId, start, end) {
    const params = new URLSearchParams({ start, end });
    return this.request(`/rooms/${roomId}/bookingOccurrences?${params}`);
  }

  async getAllBookingOccurrences() {
    return this.request('/rooms/bookingOccurrences');
  }

  // Returns ONLY the current user's active occurrences, each populated with
  // its room name. Scoped server-side — no client-side filtering required.
  async getMyBookings() {
    return this.request('/bookings/mine');
  }

  // Returns (lazily minting) the current user's subscribable iCal feed URL so
  // they can add their bookings to Google/Apple/Outlook calendars.
  async getCalendarFeed() {
    return this.request('/bookings/calendar-feed');
  }

  // Move a single occurrence to a new time (PATCH). Same room; payable rooms keep
  // the same duration server-side.
  async rescheduleBooking(occurrenceId, startDate, endDate) {
    return this.request(`/bookings/reschedule/${occurrenceId}`, {
      method: 'PATCH',
      body: JSON.stringify({ startDate, endDate }),
    });
  }

  async getBookingOccurrence(roomId, occurrenceId) {
    return this.request(`/rooms/${roomId}/bookingOccurrences/${occurrenceId}`);
  }

  async getMasterBooking(roomId, masterBookingId) {
    return this.request(`/rooms/${roomId}/masterBookings/${masterBookingId}`);
  }

  async updateBookingDescription(roomId, occurrenceId, description) {
    return this.request(`/rooms/${roomId}/updateDescription/bookingOccurrences/${occurrenceId}`, {
      method: 'PATCH',
      body: JSON.stringify({ description }),
    });
  }

  async deleteBooking(bookingId) {
    return this.request(`/bookings/delete/${bookingId}`, {
      method: 'DELETE',
    });
  }

  async unbookOccurrence(bookingId) {
    return this.request(`/bookings/unbook/${bookingId}`, {
      method: 'PATCH',
    });
  }

  // Marks the guest as checked in for the given occurrence. Returns the
  // updated occurrence (including checkedInAt / checkedOutAt). Authorized
  // server-side for the booking owner or an admin.
  async checkInOccurrence(occurrenceId) {
    return this.request(`/bookings/checkin/${occurrenceId}`, {
      method: 'POST',
    });
  }

  // Marks the guest as checked out for the given occurrence. Returns the
  // updated occurrence (including checkedInAt / checkedOutAt).
  async checkOutOccurrence(occurrenceId) {
    return this.request(`/bookings/checkout/${occurrenceId}`, {
      method: 'POST',
    });
  }

  // Soft-cancels the given occurrence plus all future occurrences of its
  // series. Resolves to { cancelled: <count> } on success.
  async cancelFollowingOccurrences(occurrenceId) {
    return this.request(`/bookings/cancel-following/${occurrenceId}`, {
      method: 'PATCH',
    });
  }

  // Updates the description on the master booking and its future occurrences.
  async updateSeriesDescription(masterBookingId, description) {
    return this.request(`/bookings/series-description/${masterBookingId}`, {
      method: 'PATCH',
      body: JSON.stringify({ description }),
    });
  }

  // ─── Waitlist ─────────────────────────────────────────────────────

  // Join the waitlist for a busy slot (used after a 409 conflict). The server
  // binds adminId + userId from the session; only the room + desired window are
  // sent. desiredStart/desiredEnd are datetime-local strings (same as bookRoom).
  async joinWaitlist(roomId, desiredStart, desiredEnd) {
    return this.request('/waitlist/join', {
      method: 'POST',
      body: JSON.stringify({ roomId, desiredStart, desiredEnd }),
    });
  }

  // Returns the current user's ACTIVE waitlist entries (waiting | notified),
  // each populated with its room name. Scoped server-side to the session user.
  async getMyWaitlist() {
    return this.request('/waitlist/mine');
  }

  // Soft-cancels (removes) one of the user's waitlist entries by id.
  async cancelWaitlist(id) {
    return this.request(`/waitlist/${id}`, {
      method: 'DELETE',
    });
  }

  // ─── Spaces ───────────────────────────────────────────────────────

  async getSpaces() {
    return this.request('/spaces');
  }

  async getSpace(spaceId) {
    return this.request(`/spaces/${spaceId}`);
  }

  async createSpace(data) {
    return this.request('/spaces/create', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateSpace(spaceId, data) {
    return this.request(`/spaces/update/${spaceId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteSpace(spaceId) {
    return this.request(`/spaces/delete/${spaceId}`, {
      method: 'DELETE',
    });
  }

  async assignRoomToSpace(spaceId, roomId) {
    return this.request(`/spaces/assignRoom/${spaceId}`, {
      method: 'PATCH',
      body: JSON.stringify({ roomId }),
    });
  }

  async unassignRoomFromSpace(spaceId, roomId) {
    return this.request(`/spaces/unassignRoom/${spaceId}`, {
      method: 'PATCH',
      body: JSON.stringify({ roomId }),
    });
  }

  async saveFloorPlan(spaceId, floorPlanData) {
    return this.request(`/spaces/saveFloorPlan/${spaceId}`, {
      method: 'POST',
      body: JSON.stringify({ floorPlan: floorPlanData }),
    });
  }

  // ─── Admins (Owner only) ──────────────────────────────────────────

  async getAdmins() {
    return this.request('/admins');
  }

  async createAdmin(data) {
    return this.request('/admins/create', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateAdmin(adminId, data) {
    return this.request(`/admins/update/${adminId}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getNoShowSettings() {
    return this.request('/admins/settings/no-show');
  }

  async updateNoShowSettings(data) {
    return this.request('/admins/settings/no-show', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Session-scoped operator (admin) email-alert settings for the logged-in
  // admin. Returns { alertOnNewBooking, alertOnCancellation } (booleans;
  // default OFF server-side). Opt-in: no alert email is sent until enabled.
  async getAlertSettings() {
    return this.request('/admins/settings/alerts');
  }

  async updateAlertSettings(data) {
    return this.request('/admins/settings/alerts', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteAdmin(adminId) {
    return this.request(`/admins/delete/${adminId}`, {
      method: 'DELETE',
    });
  }

  // ─── Payment / Stripe ─────────────────────────────────────────────

  async getStripePublicKey() {
    return this.request('/paymentDetails/stripe-public-key');
  }

  async getRoomPaymentInfo(roomId) {
    return this.request(`/paymentInfos/room-payment-info/${roomId}`);
  }

  async updatePaymentInfo(data) {
    return this.request('/paymentInfos/update', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async createPayment(data) {
    return this.request('/paymentDetails/create-payment', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getPaymentDetails(paymentDetailsId) {
    return this.request(`/paymentDetails/${paymentDetailsId}`);
  }

  // ─── Pricing rules (peak / dynamic pricing — admin) ────────────────
  // List/create/update/delete a room's peak-pricing rules. Each rule scales the
  // room's hourly rate by `multiplier` for occurrences inside its windows
  // (dayOfWeek[], hourStart/hourEnd, dateStart/dateEnd). A room with no rules is
  // priced flat (unchanged). All tenant-scoped + bookaroom-guarded server-side.

  async getPricingRules(roomId) {
    return this.request(`/pricing-rules/room/${roomId}`);
  }

  async createPricingRule(roomId, data) {
    return this.request(`/pricing-rules/room/${roomId}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updatePricingRule(ruleId, data) {
    return this.request(`/pricing-rules/${ruleId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deletePricingRule(ruleId) {
    return this.request(`/pricing-rules/${ruleId}`, {
      method: 'DELETE',
    });
  }

  // ─── Credit wallet (hours / credit packs) ──────────────────────────
  // Read the user's prepaid balance, start a Stripe top-up (drives the same
  // PaymentForm flow), and pay an awaiting-payment booking from credit. With a
  // zero balance the booking flow never calls payFromCredit and uses Stripe.

  async getCreditBalance() {
    return this.request('/credit/balance');
  }

  // Create a Stripe PaymentIntent to add `amountCents` (rappen) of credit. On
  // webhook success the wallet is credited. Returns { clientSecret,
  // paymentDetailsId, amount, ... } like createPayment so PaymentForm can reuse it.
  async createCreditTopup(amountCents, redirectUrl) {
    return this.request('/credit/topup', {
      method: 'POST',
      body: JSON.stringify({ amountCents, redirectUrl }),
    });
  }

  // Pay a payable booking entirely from wallet credit (debits the wallet and
  // activates the booking). Resolves { ok, paid, amount, balanceCents } or throws
  // with status 402 when the balance is insufficient (caller falls back to Stripe).
  async payBookingFromCredit(bookingId) {
    return this.request('/credit/pay-booking', {
      method: 'POST',
      body: JSON.stringify({ bookingId }),
    });
  }

  // ─── Settings ─────────────────────────────────────────────────────

  async getSettings() {
    return this.request('/settings');
  }

  async saveLanguage(language) {
    return this.request('/users/language', {
      method: 'PUT',
      body: JSON.stringify({ language }),
    });
  }

  async setSmsAuth(enabled) {
    return this.request('/settings/sms-auth', {
      method: 'POST',
      body: JSON.stringify({ enabled }),
    });
  }

  // ─── Chat / AI Bot ────────────────────────────────────────────────

  async sendBookingMessage(message, reset = false) {
    return this.request('/chat/booking-agent', {
      method: 'POST',
      body: JSON.stringify({ message, reset }),
    });
  }

  async transcribeAudio(base64Audio, mimeType) {
    return this.request('/chat/transcribe', {
      method: 'POST',
      body: JSON.stringify({ audio: base64Audio, mimeType }),
    });
  }

  // ─── Test User ────────────────────────────────────────────────────

  async createTestUser(data) {
    return this.request('/testUserCreate/create', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async checkTestAccessCode(accessCode) {
    return this.request(`/testUserCreate/check-access-code/${accessCode}`);
  }
}

// Singleton – one instance shared across entire app
export default new ApiService();
